<template>
  <div>
    <div
      v-for="relation in accountRelations"
      :key="relation.clientIdOrId"
      v-tooltip="tooltipMessage(relation)"
    >

      <collapsible-section
        v-if="isEntityAccountRelation(relation)"
        class="collapse-item"
        :id="`objectModelRelationshipsCollapse-${relation.client_id}`"
        control-name="objectModelRelationshipsCollapse"
        :is-open="false"
      >
        <template #section-header>
          <row>
            <prefix>
              <icon glyph="relationships"/>
            </prefix>
            <column>
              {{ relation.label_title }}
            </column>
          </row>
        </template>
        <template v-if="fromModelView" #section-contents>
          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityOwnerName)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                Entity Owner Name
              </column>
            </row>
          </field-item>

          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityOwnerEmail)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                Entity Owner Email
              </column>
            </row>
          </field-item>

          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityName)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                Entity Name
              </column>
            </row>
          </field-item>

          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityPhone)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                Entity Phone
              </column>
            </row>
          </field-item>

          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityAddress)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                Entity Address
              </column>
            </row>
          </field-item>
        </template>

        <template v-else-if="!fromModelView" #section-contents>
          <field-item
            v-if="showEntityOption"
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.Entity)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                {{ relation.label_title }} (entity)
              </column>
            </row>
          </field-item>

          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityOwner)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                {{ relation.label_title }} (owner)
              </column>
            </row>
          </field-item>

          <field-item
            :disabled="false"
            @click="select(handler.selectAccountRelation(relation, completePathForMetadata(relation, AccountRelationMetadataType.EntityMembers)))"
          >
            <row v-tooltip="tooltipMessage(relation)">
              <prefix>
                <icon glyph="member"/>
              </prefix>
              <column>
                {{ relation.label_title }} (members)
              </column>
            </row>
          </field-item>
        </template>
      </collapsible-section>

      <field-item
        v-else
        :disabled="handler.isDisabled(relation, computedChildTreeContext)"
        @click="select(handler.selectAccountRelation(relation, completePathFor(relation)))"
      >
        <row
          v-tooltip="tooltipMessage(relation)"
        >
          <prefix>
            <icon glyph="member"/>
          </prefix>
          <column>
            {{relation.labelTitleDisplay}}
          </column>
        </row>
      </field-item>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import handlerComponentsMixin from '../mixins/handlerComponentsMixin';
import ObjectModel from "~/models/objectModel";
import FieldItem from './common/field-item.vue';
import {ReferencePathNode} from "~/models/referencePaths/referencePathNode";
import {ReferenceNodeType} from "~/enums/referenceNodeType";
import {IDynamicTreeContext} from "~/interfaces/dynamicFieldsInterfaces";
import handlerTreeContextMixin from "~/components/dynamicFieldsTrees/mixins/handlerTreeContextMixin";
import ObjectModelRelation from '~/models/objectModelRelations/objectModelRelation'
import ObjectModelAccountRelationDynamicFieldsHandler from '~/models/dynamicFields/handlers/objectModelAccountRelationDynamicFieldsHandler'
import ProcessFlowObjectModelDynamicFieldsHandler from '~/models/dynamicFields/handlers/processFlows/processFlowObjectModelDynamicFieldsHandler';
import {ObjectModelRelationType} from "~/enums/objectModelRelationType";
import CollapsibleSection from './common/collapsible-section.vue';
import {AccountRelationMetadataType} from "~/enums/accountRelationMetadataType";

export default Vue.extend({
  mixins: [handlerComponentsMixin, handlerTreeContextMixin],
  components: {
    CollapsibleSection,
    FieldItem,
  },
  data: () => {
    return {
      AccountRelationMetadataType,
    }
  },
  props: {
    handler: {
      type: Object as () => (ProcessFlowObjectModelDynamicFieldsHandler | ObjectModelAccountRelationDynamicFieldsHandler),
      required: true,
    },
    objectModel: {
      type: Object as () => ObjectModel,
      required: true,
    },
    level: {
      type: Number,
      default: 1,
    },
    defaultOpen: {
      type: Boolean,
      default: false,
    },
    path: {
      type: Array as () => ReferencePathNode[],
      required: false,
    },
    fromModelView: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    treeContext(): IDynamicTreeContext {
      return {
        parent: this.objectModel,
        isTopLevelCollectionAssigned: this.parentTreeContext.isTopLevelCollectionAssigned || this.handler.isTopLevelCollection(this.objectModel, this.parentTreeContext),
        referencePathNodesToLevel: this.path,
      }
    },
    objectModelContext(): IDynamicTreeContext {
      return {
        ...this.parentTreeContext,
        referencePathNodesToLevel: this.path,
      };
    },
    accountRelations(): ObjectModelRelation[] {
      return this.handler.objectModelAccountRelations(this.objectModel)
    },
    showEntityOption(): boolean {
      return this.handler.accountRelationMetadataShowEntity;
    }
  },
  methods: {
    completePathFor(relation: ObjectModelRelation): ReferencePathNode[] {
      if (!this.path) return null;
      return [
        ...this.path,
        new ReferencePathNode(null, {
          reference_type: ReferenceNodeType.ObjectModelRelation,
          reference_id: relation.id
        }),
      ]
    },
    completePathForMetadata(relation: ObjectModelRelation, metadata: string): ReferencePathNode[] {
      if (!this.path) return null;
      return [
        ...this.path,
        new ReferencePathNode(null, {
          reference_type: ReferenceNodeType.ObjectModelRelation,
          reference_id: relation.id
        }),
        new ReferencePathNode(null, {
          reference_type: ReferenceNodeType.AccountRelationMetadata,
          reference_id: metadata
        })
      ]
    },
    tooltipMessage(relation: ObjectModelRelation): string {
      return this.handler.tooltipMessage(relation, this.computedChildTreeContext)
    },
    isEntityAccountRelation(relation: ObjectModelRelation): boolean {
      return relation.relation_type === ObjectModelRelationType.EntityAccount;
    },
  },
});
</script>